import React, { useState, useCallback, useMemo } from "react";
import InfoIcon from "assets/icons/info.svg?react";
import { useUserStatus } from "hooks/useUserStatus";
import { useMatchPathCallback } from "hooks/useMatchPathCallback";
import { useSubscriptionStatusBannerMessageProps } from "./hooks/useSubscriptionStatusBannerMessageProps";
import { Banner } from "../../Banner";
import { useHasPlanRole } from "hooks/useHasPlanRole";
import { GatedContentStatus } from "store/stripeConnectAccount/types";
import { useSelector } from "react-redux";
import { RootState } from "store/reducers";
import { usePauseStatus } from "hooks/usePauseStatus";

export const SubscriptionStatusBanner: React.FC = () => {
    const [hide, setHide] = useState(false);
    const isGrowUser = useHasPlanRole("Grow");

    const close = useCallback(() => {
        setHide(true);
    }, []);

    const {
        userStatusIsActive,
        userStatusIsTrialing,
        userPlanIsTrial,
        userIsPaused,
        userHasNoSubscription
    } = useUserStatus();

    const { userIsOrWillPause } = usePauseStatus();

    const { isPollingStripeAccounts } = useSelector(
        (state: RootState) => state.stripeConnectAccount
    );

    useMatchPathCallback(/^\/subscription\/subscribe\/[a-zA-Z0-9]+$/, close);
    const { messageContentState, gatedContentStatus } =
        useSubscriptionStatusBannerMessageProps(close);

    const showSubBanner = useMemo<boolean>(
        () =>
            (!userStatusIsActive && !userStatusIsTrialing) ||
            (userStatusIsTrialing && !isGrowUser) ||
            userPlanIsTrial ||
            userHasNoSubscription ||
            userIsPaused ||
            userIsOrWillPause ||
            (!isPollingStripeAccounts &&
                gatedContentStatus ===
                    GatedContentStatus.STRIPE_ACCOUNT_UNVERIFIED),
        [
            userStatusIsActive,
            userStatusIsTrialing,
            isGrowUser,
            userPlanIsTrial,
            userHasNoSubscription,
            userIsPaused,
            userIsOrWillPause,
            isPollingStripeAccounts,
            gatedContentStatus
        ]
    );

    const canCloseBanner = useMemo(() => {
        if (messageContentState?.preventDismiss) {
            return false;
        }

        return (
            !userHasNoSubscription &&
            gatedContentStatus !== GatedContentStatus.STRIPE_ACCOUNT_UNVERIFIED
        );
    }, [
        messageContentState?.preventDismiss,
        userHasNoSubscription,
        gatedContentStatus
    ]);

    return (
        showSubBanner && (
            <Banner
                header={messageContentState?.leftMessageProps?.key}
                subheader={messageContentState?.leftMessageProps?.subheader}
                buttonProps={messageContentState?.buttonProps}
                linkProps={messageContentState?.linkProps}
                rightMessage={messageContentState?.rightMessage}
                key="subscription-status-banner"
                icon={!messageContentState.hideIcon ? <InfoIcon /> : <></>}
                cssClass={
                    messageContentState?.cssClass ?? "subscription-status"
                }
                hide={hide}
                setHide={close}
                canClose={canCloseBanner}
            />
        )
    );
};
